import React from "react";
import { useApp } from "../services/app-context";
import { getPerformanceColor } from "../services/utils";
import { twMerge } from "tailwind-merge";


interface CircularProgressProps {
  percentage: number;
  radius?: number;
  strokeWidth?: number;
  customText?: string | React.ReactNode;
  containerClassName?: string;
  color?: {
    progressBg: string;
    progress: string;
    text: string;
  };
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percentage,
  radius = 40,
  strokeWidth = 6,
  customText,
  containerClassName,
  color: colorProps,
}: CircularProgressProps) => {
  const { tenantProfile } = useApp();
  const metric_ranges = tenantProfile?.metric_ranges || [];
  const color = colorProps || getPerformanceColor(percentage, metric_ranges || []);
  const normalizedRadius = radius - strokeWidth / 2; // Adjusted for stroke width
  const circumference = 2 * Math.PI * normalizedRadius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={twMerge("relative", containerClassName)}
      style={{ textAlign: "center", fontFamily: "Arial, sans-serif" }}
    >
      <svg
        height={radius * 2}
        width={radius * 2}
        style={{ transform: "rotate(-90deg)" }}
      >
        {/* Background Circle */}
        <circle
          stroke={color.progressBg}
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* Foreground Circle */}
        <circle
          stroke={color.progress}
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      {/* Label and Percentage */}
      <div className={twMerge("absolute label top-0 left-0 bottom-0 right-0 flex justify-center items-center")}>
        <div>{customText ? customText : `${percentage}%`}</div>
      </div>
    </div>
  );
};

export default CircularProgress;
